// THE contract. Every printed surface on every tier is a PrintDocument, and the
// three emitters (Go gateway ESC/POS, Go gateway ePOS-XML, Dart POS
// direct-Epson) only turn blocks into bytes.
//
// THE CENTRAL INVARIANT — the composer owns CONTENT, the emitter owns GEOMETRY.
// The composer decides what the words are, including formatted money and
// formatted clock times. The emitter decides where they sit: column counts,
// padding, wrapping, character sizes, code-page bytes. Nothing in this file may
// hold a column count, a padded string, or a pre-wrapped line — which is what
// makes a document composed for 80 mm print correctly on the 76 mm impact
// backup a `backupPrinterId` failover redirects it to.
//
// Money is integer minor units at the composer input and a FORMATTED STRING
// inside a block — never a float, never dollars.

/**
 * The ONE document version (2026-09-27): the LEGACY-EXACT layout — `cmd`,
 * `text`, `cols`, `fill`, `writeIn`, `feed`, `qrCode`, `wrap`, `measure`, plus
 * `logo`, `alert` and `drawerPulse` and the per-block `only` filter (see
 * "Legacy-exact layout" below). Every composer stamps it and every emitter
 * prints it and nothing else.
 *
 * Version 1 — the SEMANTIC layout (`line`, `row`, `item`, `courseSection`,
 * `totals`, `cut`, …), where the emitter chose the command sequence — is
 * RETIRED (print-legacy retirement S4). It was never the legacy paper the owner
 * fixed as the spec, no writer has composed it for a paper type since R1, and
 * every emitter refuses it: gateway since S3 (`printdoc.Validate`), the till
 * since S2b (`paperDocumentFor`), and `validatePrintDocument` here.
 */
export const PRINT_DOCUMENT_VERSION_LEGACY_EXACT = 2;

/** Highest document version an emitter in this change set understands. */
export const PRINT_DOCUMENT_MAX_SUPPORTED_VERSION = 2;

// ─── Document kinds ──────────────────────────────────────────────────────────

/**
 * What the paper IS. Mirrors the gateway's PrintType* constants plus
 * `fire_ticket`, which is a document kind but deliberately NOT a wire printType
 * — a fire ticket rides printType 'kitchen_ticket'.
 */
export type PrintDocumentKind =
  | 'kitchen_ticket'
  | 'bar_ticket'
  | 'void_kitchen_ticket'
  | 'fire_ticket'
  | 'expo_ticket'
  | 'guest_check'
  | 'customer_receipt'
  | 'merchant_receipt'
  | 'tip_adjustment'
  | 'void_receipt'
  | 'refund_receipt'
  | 'payment_action'
  | 'gift_card_receipt'
  | 'delivery_label'
  | 'platform_order'
  | 'x_report'
  | 'z_report'
  | 'shift_report'
  | 'test_print';

// ─── Paper capability (EMITTER INPUT — never part of the document) ───────────

export type PaperWidthMmValue = 58 | 76 | 80;
export type TicketFontSizeValue = 'Small' | 'Medium' | 'Large';
export type PrinterRenderModeValue = 'text' | 'raster';

/**
 * What the hardware in front of the paper can do.
 *
 * Deliberately NOT a field of PrintDocument and NEVER serialized into the RTDB
 * payload: it is resolved by whichever emitter ends up holding the job, so a
 * redirect to a different printer re-resolves it. Baking it into the document
 * is how a redirected job would print at the wrong width.
 */
export interface PrintCapability {
  /** Physical paper width. Models Printer.ts PaperWidthMm. */
  readonly paperWidthMm: PaperWidthMmValue;
  /**
   * columnsForPaperWidth: 58→32, 76→40, 80→48 — the Font A count. A stream
   * that selects Font B (a "Small" size setting) lays out at
   * paperWidthSmallFontColumns instead: 58→42, 76→40, 80→64.
   */
  readonly columns: number;
  /** Printable dots. Absent = no raster path. */
  readonly dots?: number;
  /**
   * Can the head reach the ribbon's second colour — derived from
   * paperWidthMm === 76 (TM-U220B), the fleet's only impact and only
   * two-colour device. A CAPABILITY, never a preference.
   */
  readonly twoColor: boolean;
  /** Raster logo + GS v 0 available. */
  readonly graphics: boolean;
  /** QR symbol available. */
  readonly qr: boolean;
  /** GS V 66 3 available. */
  readonly cutter: boolean;
  /** RJ11 ports wired to this printer (cash drawers + dk-port buzzers). */
  readonly drawerPorts: number;
  /** Printer has its own beeper (ESC B n t). */
  readonly buzzer: boolean;
  /**
   * Can issue DLE DC4 (real-time drawer pulse). FALSE on the POS direct-Epson
   * tier by construction: the ePOS2 SDK owns the socket and exposes no
   * real-time kick. That asymmetry is deliberate — do not "unify" it.
   */
  readonly realtimeDrawerPulse: boolean;
  /** The only code page both fleet models support. */
  readonly codePage: 'PC850';
  readonly kitchenFontSize: TicketFontSizeValue;
  readonly receiptFontSize: TicketFontSizeValue;
  readonly renderMode: PrinterRenderModeValue;
}


// ─── Text roles ──────────────────────────────────────────────────────────────

/**
 * The SEMANTIC size slots a `size:role:<role>` command names. The emitter maps a
 * role to BOTH a character size and the COLUMN COUNT that size actually has —
 * those two are one decision, and separating them is what pushes the clock off
 * the paper. The printer's kitchen / receipt font-size setting decides the size.
 */
export type PrintTextRole =
  | 'body'
  | 'caption'
  | 'heading'
  | 'display'
  | 'total'
  | 'ticketHeader'
  | 'ticketItem'
  | 'ticketModifier';

// ─── Course state ────────────────────────────────────────────────────────────

/**
 * FIRE / HOLD — the state word of a course banner ("FIRE Entrees"), produced
 * once by `courseBannerText` (documentBuilder.ts) and read by the kitchen
 * ticket engine. The banner itself is printed with `text` blocks.
 */
export type PrintCourseState = 'fire' | 'hold' | 'none';

// ─── Non-text blocks ─────────────────────────────────────────────────────────

/**
 * Image and text are ALTERNATIVES, not a stack: printing both puts the same
 * mark on the receipt twice. Ignored entirely on impact printers.
 */
export interface PrintLogoBlock {
  readonly kind: 'logo';
  readonly imageBase64?: string;
  readonly fallbackText?: string;
}

/**
 * A RESOLVED sound: pattern normalisation, repeat/cycle clamping, rule lookup
 * and QUIET HOURS are policy the composer owns. Consequence, deliberate: quiet
 * hours are evaluated at COMPOSE time, so a job queued at 21:59 and printed at
 * 22:01 still buzzes — the sound belongs to the ticket, not to the appliance's
 * clock.
 */
export interface PrintAlertSpec {
  readonly mode: 'printerBuzzer' | 'dkPort';
  readonly pattern: 'patternA' | 'patternB' | 'patternC' | 'patternD' | 'patternE';
  readonly repeat: number;
  readonly cycleMs: number;
  readonly pin: number;
  readonly pulseMs: number;
  /** Audit context only. */
  readonly trigger?: string;
}

/**
 * POSITION IN THE BLOCK LIST IS FIRING TIME. Composers emit it AFTER the
 * `cut` command, adopting the gateway's placement, so the sound means "there is a ticket in
 * your hand" on every tier.
 */
export interface PrintAlertBlock {
  readonly kind: 'alert';
  readonly alert: PrintAlertSpec;
}

/**
 * `realtime` is INTENT. A gateway honours it with DLE DC4; the POS cannot and
 * emits the buffered ESC p in the same position. Do not unify the two.
 */
export interface PrintDrawerPulseBlock {
  readonly kind: 'drawerPulse';
  readonly pin: number;
  readonly pulseMs: number;
  readonly realtime: boolean;
  /** ICashDrawer.guid. Audit only; the emitter never resolves it. */
  readonly drawerGuid?: string;
}

// ─── Legacy-exact layout (v2) ────────────────────────────────────────────────
//
// OWNER DECISION 2026-09-27: the PrintDocument becomes the only print format,
// and the paper it produces must be BYTE-IDENTICAL to what the legacy
// renderers print today — "the legacy paper is the spec". The v1 (semantic)
// blocks this replaced carried SEMANTICS and let the emitter choose the command
// sequence, which can never reproduce two hand-written legacy renderers whose sequences
// disagree with each other (receipt `B+ SIZE1x2 … SIZE1x1 B-` vs kitchen
// `SIZE B+ … B- SIZE1x1`). So the layout is expressed IN THE CONTRACT, and the
// emitters stay dumb and identical across Go and Dart:
//
//   `cmd`   — printer STATE changes, emitted in exactly the order given.
//   `text`  — one or more lines, verbatim (split on "\n"), in the current state.
//   `cols`  — `prefix + left` against a right-aligned `right`, padded/wrapped by
//             the emitter to the CURRENT column count (the geometry stays the
//             emitter's: a v2 document still prints at any width).
//   `fill`  — `prefix` then `char` repeated to the current column count.
//   `writeIn` — `label  $____` to the current column count.
//   `feed`  — `lines` blank lines.
//
// The CURRENT column count is the paper's columns IN THE CURRENT FONT divided
// by the current character width: `size:2x*`/`scale:2x*` halve it, a
// `size:role:<role>` takes that role's column count (which is how the kitchen
// font-size setting — a printer property the composer never sees — still
// decides the sizes). The font is the one `font:*` selected: Font B (a "Small"
// size setting, or `font:B`) lays out at its own, wider count — 64 on 80 mm,
// 42 on 58 mm, the Font A 40 on the 76 mm impact head (owner decision
// 2026-09-28; paperWidthSmallFontColumns) — and a `font:*` after a size change
// re-resolves that size's count in the new font.
//
//   `qrCode` — the bare QR symbol (no heading, no alignment of its own).
//
// ePOS-Print XML is stateless per element, so its emitter TRACKS the state the
// commands set and writes one `<text>` element per content block carrying it
// (align, bold, ul, width, height) — which is the shape the legacy XML
// renderers produce, one element per logical line. A `feed` there is
// `<feed line="n"/>`; a `text` of "" is `<text>\n</text>`.
//
// THE LEGACY PAPERS DIFFER BY PROTOCOL. The gateway's ESC/POS and ePOS-XML
// receipt renderers print different item lines ("  1  Name  $38.00" vs
// "Name x 1  $38.00"), different footers and different QR sections, and the
// till's legacy kitchen builder differs from the gateway's at the tail. So in a
// v2 document ANY block may carry `only: [...]`, naming the emitters that print
// it: 'escpos' (gateway ESC/POS), 'eposxml' (gateway ePOS-Print XML), 'pos'
// (the till's command-list emitter). Absent means every emitter. An emitter
// SKIPS a block whose `only` does not name it — that is a filter, never a
// layout decision, so the emitters stay dumb.
//
// A v2 emitter adds NO implicit bytes to a v2 document: no preamble, no
// inter-item gap, no trailing reset. Every byte is a block.

/**
 * One printer state change. Strings rather than objects so the three languages
 * parse one flat vocabulary.
 *
 * STYLE AND SIZE ARE INDEPENDENT (clarified 2026-09-27, owner decision).
 * `bold±`, `underline±` and `font:*` hold until changed or `init`; `size:*` and
 * `scale:*` change the character size ONLY. Every emitter prints that meaning:
 * the gateway re-states the style in each ESC ! it sends (escpos_emphasis.go —
 * a bare ESC ! clears emphasis, underline and font on Epson printers, which is
 * how receipts printed their business name and TOTAL not bold), the till's raw
 * socket sizes with GS !, and the Epson SDK and ePOS-XML take size and style
 * as separate calls / attributes.
 *
 *   init            ESC @ + the code page (the legacy preamble)
 *   bold+ / bold-   ESC E 1/0          underline+ / underline-  ESC - 1/0
 *   invert+ / invert-  GS B 1/0        alert+ / alert-  ESC r 1/0 — ONLY on
 *                                      two-colour hardware, nothing elsewhere
 *   align:left|center|right            ESC a n
 *   size:WxH        ESC ! n            (1x1, 1x2, 2x1, 2x2)
 *   scale:WxH       GS ! n             (W,H in 1..8 — the scaled banner)
 *   size:role:R     the size THIS printer uses for role R (kitchen font setting)
 *   size:rush       the rush item slot: 2x2, columns min(item, header)
 *   font:A|B        ESC M 0/1
 *   font:receipt|font:kitchen  the font THIS printer's receipt/kitchen size setting selects
 *   feedDots:N      ESC J N            (1..255); ePOS-XML `<feed unit="N"/>`
 *   cut             GS V 66 3
 */
export type PrintCommand = string;

export interface PrintCmdBlock {
  readonly kind: 'cmd';
  readonly commands: readonly PrintCommand[];
}

export interface PrintTextBlock {
  readonly kind: 'text';
  readonly text: string;
  /**
   * Print the words EXACTLY as given — no sanitize/transliterate step before
   * the code page. For legacy renderers that wrote raw text (the till's
   * report tape passes an en dash straight to its PC850 encoder, which prints
   * "?"): the legacy paper is the spec, so the document must not "fix" it.
   * Absent = sanitized, as every legacy gateway line was.
   */
  readonly verbatim?: boolean;
}

export interface PrintColsBlock {
  readonly kind: 'cols';
  readonly left: string;
  readonly right: string;
  readonly prefix?: string;
}

/** `prefix` (verbatim) then `char` to the end of the line. */
export interface PrintFillBlock {
  readonly kind: 'fill';
  readonly char: string;
  readonly prefix?: string;
}

export interface PrintWriteInBlock {
  readonly kind: 'writeIn';
  readonly text: string;
}

export interface PrintFeedBlock {
  readonly kind: 'feed';
  readonly lines: number;
}

export interface PrintQrCodeBlock {
  readonly kind: 'qrCode';
  readonly data: string;
}

/**
 * `text` folded to the current column count — the legacy renderers'
 * `wrapHanging(text, cols, cols - len(indent))`: the first line at the full
 * width, every continuation at the width less `indent` and prefixed by it.
 * `alertColor` prints each folded line in the alert colour (ESC r 1 … ESC r 0
 * around every line, two-colour hardware only). On ePOS-XML each folded line is
 * its own `<text>` element. The key is `alertColor`, not `alert`: `alert` is
 * the alert block's spec and a shared JSON tag makes Go drop both.
 */
export interface PrintWrapBlock {
  readonly kind: 'wrap';
  readonly text: string;
  readonly indent?: string;
  readonly alertColor?: boolean;
}

/**
 * The printer's OWN column count — the one fact a document composed on the
 * server cannot know (S2a: `test_print`'s width calibration). The emitter
 * supplies it at emit time, as the CURRENT column count: exactly the number a
 * `fill` block pads to at the same point in the stream.
 *
 * Two forms:
 *
 *   `text`    (no `overrun`) one line: `text` with every `{columns}` replaced by the count in
 *             decimal ("Configured: {columns} columns" → "Configured: 48
 *             columns"), then printed as a `text` line is.
 *   `overrun` (no `text`) the two-row calibration ruler (a tens row and a units row),
 *             `overrun` columns LONGER than the count. It deliberately runs past
 *             the paper: where it wraps is the printer's true width, so a ruler
 *             exactly as wide as the configured count could only ever confirm
 *             it (escpos_test_print.go `calibrationRuler`). Past 99 columns the
 *             tens row prints '+' rather than a two-cell number.
 *
 * A NEW KIND on purpose, rather than a `{columns}` token in a `text` block: an
 * emitter that predates it REFUSES the whole document on the unknown kind and
 * prints the legacy fields beside it, where a token inside a kind it already
 * knows would print the literal "{columns}" on every device not yet updated.
 *
 * Laid out by `measureLines` (documentText.ts) — the one TypeScript copy, read
 * by the preview and pinned against Go and Dart by the `measureLines`
 * conformance op.
 */
export interface PrintMeasureBlock {
  readonly kind: 'measure';
  readonly text?: string;
  readonly overrun?: number;
}

/** The `{columns}` token of a `measure` block's `text`. */
export const PRINT_MEASURE_COLUMNS_TOKEN = '{columns}';

/** The largest `overrun` a `measure` ruler may ask for. */
export const PRINT_MEASURE_MAX_OVERRUN = 64;

/** Which emitter prints a block (v2 `only`). */
export type PrintProtocol = 'escpos' | 'eposxml' | 'pos';
export const PRINT_PROTOCOLS = ['escpos', 'eposxml', 'pos'] as const;

const COMMAND_PATTERN =
  /^(init|cut|bold[+-]|underline[+-]|invert[+-]|alert[+-]|align:(left|center|right)|size:[12]x[12]|size:rush|scale:[1-8]x[1-8]|size:role:(body|caption|heading|display|total|ticketHeader|ticketItem|ticketModifier)|font:(A|B|receipt|kitchen)|feedDots:([1-9]|[1-9][0-9]|1[0-9][0-9]|2[0-4][0-9]|25[0-5]))$/;

/** Is `c` in the command vocabulary? Pinned across languages by the conformance corpus. */
export function isPrintCommand(c: unknown): c is PrintCommand {
  return typeof c === 'string' && COMMAND_PATTERN.test(c);
}

/**
 * Any block, as it appears in a v2 document: optionally restricted to some
 * emitters (see "Legacy-exact layout").
 */
export type PrintBlockOnly = { readonly only?: readonly PrintProtocol[] };

export type PrintBlock = PrintBlockOnly & (
  | PrintLogoBlock
  | PrintAlertBlock
  | PrintDrawerPulseBlock
  | PrintCmdBlock
  | PrintTextBlock
  | PrintColsBlock
  | PrintFillBlock
  | PrintWriteInBlock
  | PrintFeedBlock
  | PrintQrCodeBlock
  | PrintWrapBlock
  | PrintMeasureBlock
);

export type PrintBlockKind = PrintBlock['kind'];

/** Every kind, for the exhaustive-switch guard and the tri-language parity test. */
export const PRINT_BLOCK_KINDS = [
  'logo', 'alert', 'drawerPulse',
  'cmd', 'text', 'cols', 'fill', 'writeIn', 'feed', 'qrCode', 'wrap', 'measure',
] as const satisfies readonly PrintBlockKind[];

// ─── Document ────────────────────────────────────────────────────────────────

/**
 * A prep station's kitchen-alert SETTINGS as the ticket was composed with them —
 * exactly the kitchen wire payload's `alert` (gateway printjob.KitchenAlert,
 * `KitchenWireAlert` in the kitchen composer). Not the sound: the sound that
 * fires is the RESOLVED `alert` block. These are the settings the gateway's
 * "kitchen alert sounded / silent" diagnostic explains a silence from (which
 * rule was off, quiet hours, the wrong mode).
 */
export interface PrintKitchenAlertSettings {
  readonly enabled: boolean;
  readonly mode: string;
  readonly pin: number;
  readonly pulseMs: number;
  readonly rules?: readonly {
    readonly trigger: string;
    readonly enabled: boolean;
    readonly pattern: string;
    readonly repeat: number;
    readonly cycleMs: number;
  }[];
  readonly quietHours?: { readonly start: string; readonly end: string } | null;
  readonly trigger?: string;
}

/**
 * Audit and telemetry ONLY. Nothing here is printed — every printed string is a
 * block. Adding a printable field here is how a second payload grows back.
 *
 * `orderType` and `kitchenAlert` (S4) are the two facts a NON-renderer reads
 * off a paper job: the gateway's remote-order appliance chime (`IsRemoteOrder`,
 * delivery / take-out) and its kitchen-alert diagnostic. They used to be read
 * from the job payload's legacy `orderType` / `alert` fields, which is what kept
 * those fields alive after the document carried the paper. A composer sets
 * `orderType` whenever its wire payload names one, and `kitchenAlert` whenever
 * its wire payload carries the station's alert settings.
 */
export interface PrintDocumentMeta {
  /** Epoch ms. */
  readonly printedAtMs: number;
  /** IANA zone the composer formatted every clock string in. */
  readonly timeZone: string;
  readonly orderNumber?: number;
  readonly orderGuid?: string;
  readonly stationName?: string;
  readonly prepStationId?: number;
  readonly courseId?: number;
  readonly correlationId?: string;
  /** The wire's order type (dineIn | takeOut | delivery | …). */
  readonly orderType?: string;
  /** The prep station's alert settings (kitchen / bar / void-kitchen chits). */
  readonly kitchenAlert?: PrintKitchenAlertSettings;
}

export interface PrintDocument {
  readonly version: number;
  readonly kind: PrintDocumentKind;
  readonly meta: PrintDocumentMeta;
  readonly blocks: readonly PrintBlock[];
}

// ─── Validation ──────────────────────────────────────────────────────────────

/**
 * Cheap structural gate every emitter runs before touching a document. Returns
 * null when the document is emittable, or the reason it is not.
 *
 * A reason means the job FAILS — paper that says the wrong thing is worse than
 * paper that never came, and since R3 there is no legacy payload to fall back
 * to. The Go (`printdoc.Validate`) and Dart (`PrintDocument.tryFromMap`) gates
 * refuse the same shapes, so no tier prints a document another refuses.
 */
export function validatePrintDocument(doc: unknown): string | null {
  if (typeof doc !== 'object' || doc === null) return 'not an object';
  const d = doc as PrintDocument;
  if (typeof d.version !== 'number') return 'missing version';
  if (d.version > PRINT_DOCUMENT_MAX_SUPPORTED_VERSION) return `version ${d.version} unsupported`;
  if (d.version < PRINT_DOCUMENT_VERSION_LEGACY_EXACT) {
    return `version ${d.version} is not a legacy-exact document (version ${PRINT_DOCUMENT_VERSION_LEGACY_EXACT})`;
  }
  const metaReason = metaProblem((d as { meta?: unknown }).meta);
  if (metaReason !== null) return metaReason;
  if (!Array.isArray(d.blocks)) return 'missing blocks';
  for (const b of d.blocks) {
    if (!(PRINT_BLOCK_KINDS as readonly string[]).includes((b as PrintBlock)?.kind)) {
      return `unknown block kind ${String((b as PrintBlock)?.kind)}`;
    }
    const kind = (b as PrintBlock).kind;
    const only = (b as PrintBlockOnly).only;
    if (only !== undefined) {
      if (!Array.isArray(only) || only.some((p) => !(PRINT_PROTOCOLS as readonly string[]).includes(p))) {
        return 'block.only must list escpos | eposxml | pos';
      }
    }
    if (kind === 'cmd') {
      const commands = (b as PrintCmdBlock).commands;
      if (!Array.isArray(commands)) return 'cmd.commands must be an array';
      const bad = commands.find((c) => !isPrintCommand(c));
      if (bad !== undefined) return `unknown print command ${String(bad)}`;
    }
    if (kind === 'measure') {
      const reason = measureBlockProblem(b as PrintMeasureBlock);
      if (reason !== null) return reason;
    }
    if (kind === 'alert' && !isPlainObject((b as { alert?: unknown }).alert)) {
      // An alert block with no sound is not an emittable block: the emitters
      // could only drop it (a silent kitchen) or refuse it at render time, after
      // the job was already claimed. Refused HERE, identically in Go and Dart.
      return 'alert block carries no alert spec';
    }
  }
  return null;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Why a document's `meta` is not acceptable, or null. Only the two keys a
 * non-renderer reads are type-checked: a non-string `orderType` or a non-object
 * `kitchenAlert` is a composer defect, and refusing it here keeps it from
 * reaching the gateway as a chime or a diagnostic that quietly reads nothing.
 */
function metaProblem(meta: unknown): string | null {
  if (meta === undefined) return null;
  if (!isPlainObject(meta)) return 'meta must be an object';
  if (meta.orderType !== undefined && typeof meta.orderType !== 'string') return 'meta.orderType must be a string';
  if (meta.kitchenAlert !== undefined && !isPlainObject(meta.kitchenAlert)) {
    return 'meta.kitchenAlert must be an object';
  }
  return null;
}

/**
 * Why a `measure` block is not emittable, or null. With `overrun` it is the
 * ruler: an integer 0..PRINT_MEASURE_MAX_OVERRUN, and no `text` beside it.
 * Without, it is the text line (`text` absent prints an empty line). The Go
 * (printdoc Validate) and Dart (MeasureBlock) gates refuse the same shapes, so
 * no tier prints a measure block another refuses.
 */
function measureBlockProblem(b: PrintMeasureBlock): string | null {
  if (b.text !== undefined && typeof b.text !== 'string') return 'measure.text must be a string';
  if (b.overrun === undefined) return null;
  if (b.text) return 'measure carries both text and overrun';
  if (!Number.isInteger(b.overrun) || b.overrun < 0 || b.overrun > PRINT_MEASURE_MAX_OVERRUN) {
    return `measure.overrun must be an integer 0..${PRINT_MEASURE_MAX_OVERRUN}`;
  }
  return null;
}
